import path from "path-browserify";
import { SANDBOX_WORKSPACE_PATH } from "../constants";

/**
 * Check if the workspace is a sandbox workspace based on file paths
 * @param {Array} files - Array of file objects
 * @returns {boolean} True if this is a sandbox workspace
 */
export const isSandboxWorkspace = (files) => {
  return files.some(
    (file) =>
      file.path.startsWith(SANDBOX_WORKSPACE_PATH + "/") ||
      file.path === SANDBOX_WORKSPACE_PATH,
  );
};

/**
 * Get the relative path for API calls
 * @param {string} filePath - The absolute file path
 * @param {string} projectPath - The project root path
 * @returns {string} The relative path for API usage
 */
export const getRelativePath = (filePath, projectPath) => {
  // For sandbox workspaces, strip the sandbox prefix
  if (filePath.startsWith(SANDBOX_WORKSPACE_PATH + "/")) {
    return filePath.replace(SANDBOX_WORKSPACE_PATH + "/", "");
  }

  // For local workspaces, calculate relative path
  if (projectPath && filePath.startsWith(projectPath)) {
    return path.relative(projectPath, filePath);
  }

  // If already relative, return as is
  return filePath;
};

/**
 * Get the effective project path based on workspace type
 * @param {Array} files - Array of file objects
 * @param {string} projectPath - The original project path
 * @returns {string} The effective project path to use
 */
export const getEffectiveProjectPath = (files, projectPath) => {
  const isSandbox = isSandboxWorkspace(files);
  return isSandbox ? SANDBOX_WORKSPACE_PATH : projectPath;
};

/**
 * Normalize a file path to absolute form
 * @param {string} filePath - The file path (may be relative or absolute)
 * @param {string} projectPath - The project root path
 * @returns {string} The normalized absolute path
 */
export const normalizeFilePath = (filePath, projectPath) => {
  // Already absolute sandbox path
  if (
    filePath.startsWith(SANDBOX_WORKSPACE_PATH + "/") ||
    filePath === SANDBOX_WORKSPACE_PATH
  ) {
    return filePath;
  }

  // Already absolute local path
  if (filePath.startsWith(projectPath)) {
    return filePath;
  }

  // Relative path - make it absolute
  return path.join(projectPath, filePath);
};

/**
 * Get the parent directory of a file path
 * @param {string} filePath - The file path
 * @returns {string} The parent directory path
 */
export const getParentDirectory = (filePath) => {
  return path.dirname(filePath);
};

/**
 * Get the file name from a path
 * @param {string} filePath - The file path
 * @returns {string} The file name
 */
export const getFileName = (filePath) => {
  return path.basename(filePath);
};

/**
 * Check if a path is a child of a parent path
 * @param {string} childPath - The potential child path
 * @param {string} parentPath - The parent path
 * @returns {boolean} True if childPath is under parentPath
 */
export const isChildPath = (childPath, parentPath) => {
  const relative = path.relative(parentPath, childPath);
  return relative && !relative.startsWith("..") && !path.isAbsolute(relative);
};

/**
 * Get directory children from a flat file list
 * @param {Array} files - Array of all file objects
 * @param {string} dirPath - The directory path to get children for
 * @returns {Array} Array of direct children (files and directories)
 */
export const getDirectoryChildren = (files, dirPath) => {
  const children = files.filter((file) => {
    const parentPath = getParentDirectory(file.path);
    return parentPath === dirPath;
  });

  return children;
};

/**
 * Sort files and directories (directories first, then alphabetically)
 * @param {Array} items - Array of file/directory objects
 * @returns {Array} Sorted array
 */
export const sortFileItems = (items) => {
  return [...items].sort((a, b) => {
    // Directories come before files
    if (a.type === "directory" && b.type !== "directory") return -1;
    if (a.type !== "directory" && b.type === "directory") return 1;
    // Then sort alphabetically by name
    return a.name.localeCompare(b.name);
  });
};

/**
 * Build a tree structure from a flat file list
 * @param {Array} files - Array of file objects
 * @param {string} rootPath - The root path to start from
 * @returns {Array} Tree structure of files and directories
 */
export const buildFileTree = (files, rootPath) => {
  const tree = [];
  const processedPaths = new Set();

  // First, identify all unique directories
  const directories = new Set();
  files.forEach((file) => {
    if (file.type === "directory") {
      directories.add(file.path);
    }
    // Also add parent directories that might not be explicitly listed
    let currentPath = getParentDirectory(file.path);
    while (
      currentPath !== rootPath &&
      currentPath !== "/" &&
      currentPath !== "."
    ) {
      directories.add(currentPath);
      currentPath = getParentDirectory(currentPath);
    }
  });

  // Build the tree recursively
  const buildNode = (nodePath) => {
    const children = getDirectoryChildren(files, nodePath);
    return children.map((child) => {
      if (child.type === "directory") {
        return {
          ...child,
          children: buildNode(child.path),
        };
      }
      return child;
    });
  };

  return buildNode(rootPath);
};

/**
 * Convert a file path for display purposes
 * @param {string} filePath - The file path
 * @param {string} projectPath - The project root path
 * @returns {string} The display path
 */
export const getDisplayPath = (filePath, projectPath) => {
  if (filePath.startsWith(SANDBOX_WORKSPACE_PATH + "/")) {
    return filePath.replace(SANDBOX_WORKSPACE_PATH, "~/workspace");
  } else if (filePath === SANDBOX_WORKSPACE_PATH) {
    return "~/workspace";
  }

  if (projectPath && filePath.startsWith(projectPath)) {
    const relative = path.relative(projectPath, filePath);
    return relative || ".";
  }

  return filePath;
};
